import { useState } from "react";
import { Form, Image, Upload, UploadProps } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import helper from "../utils/helper";

export interface DocumentUploadProps {
    title?: string;
    hint?: string;
    name?: string;
    value: any;
    onChange: (info: any) => any;
    accept?: string;
    maxCount?: number;
    uploadProps?: UploadProps;
}

const imageExtensions = /\.(png|jpe?g|gif|webp|bmp)$/i;

const isImage = (file: any) => {
    if (file?.type) return file.type.startsWith("image/");
    return imageExtensions.test((file?.name || file?.url || "").split("?")[0]);
};

const toFileList = (value: any, uid: string) => {
    if (Array.isArray(value)) {
        // Files coming straight from the api have no uid, which antd needs to track removals
        return value.every((file: any) => file?.uid)
            ? value
            : value.map((file: any, index: number) => (file?.uid ? file : { ...file, uid: `${uid}-${index}` }));
    }
    if (typeof value === "string" && value) {
        return [{
            uid,
            url: value,
            name: value.split("?")[0].split("/").pop() || "Document",
            status: "done" as const,
        }];
    }
    return [];
};

export default function DocumentUpload({
    title,
    hint = "Image",
    name,
    value,
    onChange,
    accept = "image/*",
    maxCount = 1,
    uploadProps,
}: DocumentUploadProps) {
    const [preview, setPreview] = useState({ visible: false, src: "", name: "" });
    const fileList = toFileList(value, `existing-${name || title}`);

    const handlePreview = (file: any) => {
        const src = file.url || file.thumbUrl || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : "");
        if (!src) return;
        if (!isImage(file)) {
            window.open(src, "_blank");
            return;
        }
        setPreview({ visible: true, src, name: file.name || title || "Document" });
    };

    const upload = (
        <Upload
            {...helper.dataFile}
            accept={accept}
            maxCount={maxCount}
            listType="picture-card"
            className="docUpload-upload"
            fileList={fileList}
            onChange={onChange}
            onPreview={handlePreview}
            customRequest={({ file, onSuccess }) => { setTimeout(() => onSuccess && onSuccess("ok", file as any), 300); }}
            {...uploadProps}
        >
            <div className="docUpload-trigger">
                <UploadOutlined style={{ fontSize: 20 }} />
                <span>Upload</span>
            </div>
        </Upload>
    );

    return (
        <div className="docUpload-row">
            {(title || hint) && (
                <div className="docUpload-rowLabel">
                    {title && <span className="docUpload-rowTitle">{title}</span>}
                    {hint && <span className="docUpload-rowHint">{hint}</span>}
                </div>
            )}
            <div className="docUpload-rowContent">
                {name
                    ? (
                        <Form.Item name={name} getValueFromEvent={onChange} style={{ marginBottom: 0 }}>
                            {upload}
                        </Form.Item>
                    )
                    : upload}
            </div>
            {preview.src && (
                <Image
                    src={preview.src}
                    alt={preview.name}
                    wrapperStyle={{ display: "none" }}
                    preview={{
                        visible: preview.visible,
                        onVisibleChange: (visible: boolean) => setPreview(prev => ({ ...prev, visible })),
                    }}
                />
            )}
        </div>
    );
}
